import { ExternalLink, TrendingUp } from "lucide-react";

const CASES = [
  {
    client: "Tampa Bay Roofing Co.",
    industry: "Roofing",
    image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1200&q=70",
    headline: "From 4 calls/month to 22.",
    body: "Lead Generation Website paired with local SEO and Google Business optimization. Inbound qualified inquiries grew 5.5× in the first 90 days.",
    metric: "+450% inbound leads",
    accent: "cyan",
  },
  {
    client: "Bright Path Cleaning",
    industry: "Cleaning Services",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=70",
    headline: "Booked solid in 6 weeks.",
    body: "Starter Website with conversion-focused copy and an online booking flow. Generated $14,200 in new recurring contracts within the first quarter.",
    metric: "$14.2k new revenue",
    accent: "orange",
  },
  {
    client: "Whitman Family Dental",
    industry: "Healthcare",
    image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=70",
    headline: "47 new patients in 90 days.",
    body: "Complete Marketing System with AI assistant, online appointment booking, and automated review collection. Practice reached capacity ahead of schedule.",
    metric: "47 new patients",
    accent: "lime",
  },
];

const ACCENT = {
  cyan: { text: "text-neon-cyan", border: "border-neon-cyan/40", bg: "bg-neon-cyan/5" },
  orange: { text: "text-neon-orange", border: "border-neon-orange/40", bg: "bg-neon-orange/5" },
  lime: { text: "text-neon-lime", border: "border-neon-lime/40", bg: "bg-neon-lime/5" },
};

export default function Portfolio() {
  return (
    <section id="work" data-testid="portfolio-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline mb-4">// SELECTED_WORK</div>
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Recent builds.<br />
              <span className="text-white/40">Real outcomes.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg">
            A few client projects from the past year — each one designed, built, and supported to deliver measurable results for the business behind it.
          </p>
        </div>

        <div data-testid="portfolio-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CASES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <article
                key={c.client}
                data-testid={`case-${i}`}
                className="group glass glass-hover overflow-hidden flex flex-col transition-all hover:-translate-y-1"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.client}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F16] via-[#0F0F16]/30 to-transparent" />
                  <div className={`absolute top-4 left-4 px-2.5 py-1 ${a.bg} border ${a.border} font-mono text-[10px] uppercase tracking-widest ${a.text}`}>
                    {c.industry}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <div className={`font-mono text-xs font-bold ${a.text} flex items-center gap-1.5`}>
                      <TrendingUp size={12} /> {c.metric}
                    </div>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
                    // {c.client}
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight mb-3 leading-tight">
                    {c.headline}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed flex-1">{c.body}</p>
                  <div className={`mt-5 pt-4 border-t border-white/10 font-mono text-xs uppercase tracking-widest ${a.text} opacity-60 group-hover:opacity-100 transition-opacity flex items-center gap-1.5`}>
                    Case Study <ExternalLink size={11} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-12 text-center font-mono text-xs uppercase tracking-widest text-white/30">
          Identifying details adjusted with client permission · Full case studies available on request
        </p>
      </div>
    </section>
  );
}
